import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';
import ApiClient from '../../utils/apiClient.js';

/**
 * QuickActionTemplateManager component for admin dashboard
 * Manages Quick Action templates with CRUD operations
 */
const QuickActionTemplateManager = () => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [apiClient] = useState(() => new ApiClient(getToken));

  // State management
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'preview'
  const [formData, setFormData] = useState({
    name: '',
    system_prompt: '',
    first_message: '',
    description: '',
    is_active: true,
    display_order: 0
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [reordering, setReordering] = useState(false);

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  /**
   * Fetch all templates from API
   */
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/admin/quick-action-templates');
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      showToast('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle template creation
   */
  const handleCreateTemplate = () => {
    setModalMode('create');
    setSelectedTemplate(null);
    setFormData({
      name: '',
      system_prompt: '',
      first_message: '',
      description: '',
      is_active: true,
      display_order: Math.max(...templates.map(t => t.display_order || 0), 0) + 1
    });
    setFormErrors({});
    setShowModal(true);
  };

  /**
   * Handle template editing
   */
  const handleEditTemplate = (template) => {
    setModalMode('edit');
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      system_prompt: template.system_prompt,
      first_message: template.first_message || '',
      description: template.description || '',
      is_active: template.is_active === 1,
      display_order: template.display_order || 0
    });
    setFormErrors({});
    setShowModal(true);
  };

  /**
   * Handle template preview
   */
  const handlePreviewTemplate = (template) => {
    setModalMode('preview');
    setSelectedTemplate(template);
    setShowModal(true);
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Template name is required';
    } else if (formData.name.length > 100) {
      errors.name = 'Template name must be 100 characters or less';
    }

    if (!formData.system_prompt.trim()) {
      errors.system_prompt = 'System prompt is required';
    }

    if (!formData.first_message || !formData.first_message.trim()) {
      errors.first_message = 'First message is required';
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = 'Description must be 500 characters or less';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        system_prompt: formData.system_prompt.trim(),
        first_message: formData.first_message.trim(),
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        display_order: formData.display_order
      };

      if (modalMode === 'create') {
        await apiClient.post('/api/admin/quick-action-templates', payload);
        showToast('Template created successfully', 'success');
      } else if (modalMode === 'edit') {
        await apiClient.put(`/api/admin/quick-action-templates/${selectedTemplate.id}`, payload);
        showToast('Template updated successfully', 'success');
      }

      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      showToast(error.message || 'Failed to save template', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle template activation/deactivation
   */
  const handleToggleActive = async (template) => {
    const action = template.is_active ? 'deactivate' : 'activate';
    const confirmMessage = `Are you sure you want to ${action} the template "${template.name}"?`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await apiClient.put(`/api/admin/quick-action-templates/${template.id}`, {
        name: template.name,
        system_prompt: template.system_prompt,
        first_message: template.first_message || '',
        description: template.description,
        is_active: !template.is_active,
        display_order: template.display_order
      });
      
      showToast(`Template ${action}d successfully`, 'success');
      fetchTemplates();
    } catch (error) {
      console.error(`Failed to ${action} template:`, error);
      showToast(`Failed to ${action} template`, 'error');
    }
  };

  /**
   * Handle template deletion
   */
  const handleDeleteTemplate = async (template) => {
    const confirmMessage = `Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`;
    
    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await apiClient.delete(`/api/admin/quick-action-templates/${template.id}`);
      showToast('Template deleted successfully', 'success');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      showToast('Failed to delete template', 'error');
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Handle drag start
   */
  const handleDragStart = (e, template) => {
    setDraggedItem(template);
    e.dataTransfer.effectAllowed = 'move';
  };

  /**
   * Handle drag over
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  /**
   * Handle drop
   */
  const handleDrop = async (e, targetTemplate) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.id === targetTemplate.id) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = templates.findIndex(t => t.id === draggedItem.id);
    const targetIndex = templates.findIndex(t => t.id === targetTemplate.id);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    // Create new array with reordered items
    const newTemplates = [...templates];
    const [removed] = newTemplates.splice(draggedIndex, 1);
    newTemplates.splice(targetIndex, 0, removed);

    // Update display_order values
    const updatedTemplates = newTemplates.map((template, index) => ({
      ...template,
      display_order: index + 1
    }));

    // Optimistically update UI
    setTemplates(updatedTemplates);
    setDraggedItem(null);

    // Send reorder request to backend
    try {
      setReordering(true);
      await apiClient.put('/api/admin/quick-action-templates/reorder', {
        templates: updatedTemplates.map(t => ({
          id: t.id,
          display_order: t.display_order
        }))
      });
      showToast('Templates reordered successfully', 'success');
    } catch (error) {
      console.error('Failed to reorder templates:', error);
      showToast('Failed to reorder templates', 'error');
      // Revert on error
      fetchTemplates();
    } finally {
      setReordering(false);
    }
  };

  /**
   * Handle drag end
   */
  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (isActive) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    
    if (isActive) {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
          : 'bg-green-100 text-green-800 border border-green-200'
      }`;
    } else {
      return `${baseClasses} ${
        theme === 'dark' 
          ? 'bg-red-900/30 text-red-400 border border-red-500/30' 
          : 'bg-red-100 text-red-800 border border-red-200'
      }`;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Quick Action Templates
            </h2>
            <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage system prompt templates for Quick Actions
            </p>
          </div>
        </div>
        <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg p-8`}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Quick Action Templates
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage system prompt templates for Quick Actions. Drag and drop rows to reorder templates.
          </p>
        </div>
        <button
          onClick={handleCreateTemplate}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
        >
          Create Template
        </button>
      </div>

      {/* Templates Table */}
      <div className={`${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } rounded-lg border overflow-hidden shadow-sm`}>
        {templates.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">📝</div>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
              No templates found
            </h3>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
              Create your first Quick Action template to get started.
            </p>
            <button
              onClick={handleCreateTemplate}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
            >
              Create Template
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className={`px-4 py-3 ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Templates ({templates.length})
              </h3>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className={`w-full ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <thead className={`${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                  <tr>
                    <th className={`px-2 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider w-8`}>
                      Order
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      Name
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      First Message Preview
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      System Prompt Preview
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      Status
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      Created
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      Last Edited
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } uppercase tracking-wider`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`${theme === 'dark' ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'} divide-y`}>
                  {templates.map((template, index) => (
                    <tr
                      key={template.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, template)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, template)}
                      onDragEnd={handleDragEnd}
                      className={`transition-colors cursor-move ${
                        theme === 'dark' ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                      } ${draggedItem?.id === template.id ? 'opacity-50' : ''} ${reordering ? 'pointer-events-none' : ''}`}
                    >
                      <td className="px-2 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <div className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {template.display_order || index + 1}
                          </div>
                          <div className={`ml-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div>
                          <div className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {template.name}
                          </div>
                          {template.description && (
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {template.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} max-w-xs`}>
                          <div className="truncate">
                            {template.first_message && template.first_message.length > 100 
                              ? `${template.first_message.substring(0, 100)}...`
                              : template.first_message || 'No first message'
                            }
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} max-w-xs`}>
                          <div className="truncate">
                            {template.system_prompt.length > 100 
                              ? `${template.system_prompt.substring(0, 100)}...`
                              : template.system_prompt
                            }
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(template.is_active === 1)}>
                          {template.is_active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDate(template.created_at)}
                      </td>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {formatDate(template.updated_at)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm space-x-2">
                        <button
                          onClick={() => handlePreviewTemplate(template)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Preview template"
                        >
                          Preview
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-teal-600 hover:text-teal-800 transition-colors"
                          title="Edit template"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(template)}
                          className={`transition-colors ${
                            template.is_active === 1
                              ? 'text-yellow-600 hover:text-yellow-800'
                              : 'text-green-600 hover:text-green-800'
                          }`}
                          title={template.is_active === 1 ? 'Deactivate template' : 'Activate template'}
                        >
                          {template.is_active === 1 ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Delete template"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          } rounded-lg border shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {modalMode === 'create' && 'Create Template'}
                  {modalMode === 'edit' && 'Edit Template'}
                  {modalMode === 'preview' && 'Template Preview'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className={`${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                >
                  <span className="sr-only">Close</span>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              {modalMode === 'preview' ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Template Name
                    </label>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <p className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTemplate?.name}
                      </p>
                    </div>
                  </div>
                  
                  {selectedTemplate?.description && (
                    <div>
                      <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        Description
                      </label>
                      <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <p className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          {selectedTemplate.description}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      First Message
                    </label>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} max-h-32 overflow-y-auto`}>
                      <pre className={`whitespace-pre-wrap text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTemplate?.first_message || 'No first message defined'}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      System Prompt
                    </label>
                    <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} max-h-64 overflow-y-auto`}>
                      <pre className={`whitespace-pre-wrap text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {selectedTemplate?.system_prompt}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Status
                    </label>
                    <span className={getStatusBadge(selectedTemplate?.is_active === 1)}>
                      {selectedTemplate?.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Template Name */}
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${formErrors.name ? 'border-red-500' : ''}`}
                      placeholder="Enter template name"
                      maxLength={100}
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${formErrors.description ? 'border-red-500' : ''}`}
                      placeholder="Enter template description (optional)"
                      maxLength={500}
                    />
                    {formErrors.description && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                    )}
                  </div>

                  {/* First Message */}
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      First Message *
                    </label>
                    <textarea
                      value={formData.first_message}
                      onChange={(e) => setFormData({ ...formData, first_message: e.target.value })}
                      rows={4}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${formErrors.first_message ? 'border-red-500' : ''}`}
                      placeholder="Enter the first message that will be sent when using this template"
                    />
                    {formErrors.first_message && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.first_message}</p>
                    )}
                  </div>

                  {/* System Prompt */}
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      System Prompt *
                    </label>
                    <textarea
                      value={formData.system_prompt}
                      onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                      rows={8}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } ${formErrors.system_prompt ? 'border-red-500' : ''}`}
                      placeholder="Enter the system prompt for this template"
                    />
                    {formErrors.system_prompt && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.system_prompt}</p>
                    )}
                  </div>

                  {/* Display Order */}
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-300'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      }`}
                      placeholder="Enter display order (lower numbers appear first)"
                      min="0"
                    />
                    <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Lower numbers appear first. You can also drag and drop rows to reorder.
                    </p>
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded dark:border-gray-600"
                    />
                    <label htmlFor="is_active" className={`ml-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Active (available for use in Quick Actions)
                    </label>
                  </div>
                </form>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} flex justify-end space-x-3`}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {modalMode === 'preview' ? 'Close' : 'Cancel'}
              </button>
              {modalMode !== 'preview' && (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
                >
                  {submitting ? 'Saving...' : modalMode === 'create' ? 'Create Template' : 'Update Template'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickActionTemplateManager;
